import React, { useEffect, useMemo, useState } from 'react';

import dayjs from 'dayjs';
import ReportCard from './ReportCard';
import { useOwnerInspectionActivityLazyQuery } from '../../queries';
import { baseUrl } from '../../utils/EnvironmentService';

type Props = {
  site?: { id: string; name: string };
  fromDate?: dayjs.Dayjs;
  toDate?: dayjs.Dayjs;
};

export default function FMInspectionAcivityReportCard({ site, fromDate, toDate }: Props) {
  const [
    generateReport,
    { data: reportGeneratedData, loading: generatingReport },
  ] = useOwnerInspectionActivityLazyQuery({ fetchPolicy: 'no-cache' });
  const [lastDownloadedDocument, setLastDownloadedDocument] = useState<string | null>(null);

  const filename = useMemo(() => {
    if (!fromDate || !toDate) {
      return '';
    }
    const siteName = site?.name ?? 'AllSites';
    const from = fromDate.format('YYYYMMDD');
    const to = toDate.format('YYYYMMDD');

    return `InspectionActivity_${siteName}_${from}_${to}.csv`.replaceAll(' ', '');
  }, [site, fromDate, toDate]);

  useEffect(() => {
    if (
      reportGeneratedData?.ownerInspectionActivity.path &&
      reportGeneratedData.ownerInspectionActivity.path !== lastDownloadedDocument
    ) {
      let url = `${baseUrl}${reportGeneratedData.ownerInspectionActivity.path}`;

      setLastDownloadedDocument(reportGeneratedData.ownerInspectionActivity.path ?? null);
      // fetch data from url
      fetch(url, {
        credentials: 'include',
      })
        .then((response) => response.blob())
        .then((blob) => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch(console.error);
    }
  }, [reportGeneratedData, filename, lastDownloadedDocument]);

  const onGenerate = () => {
    generateReport({
      variables: {
        siteId: site?.id,
        reportPeriodFrom: fromDate?.startOf('day').toDate(),
        reportPeriodTo: toDate?.endOf('day').toDate(),
      },
    });
  };

  return (
    <ReportCard
      title="Inspection activity"
      description="A report of inspection activity based on site selection and date range"
      requiresDateRange
      site={site}
      fromDate={fromDate?.toDate()}
      toDate={toDate?.toDate()}
      onGenerateReport={onGenerate}
      disabled={generatingReport}
    />
  );
}
